import {FunctionsHttpError} from '@supabase/supabase-js';
import {Database} from '@tnc/shared';
import {useRef, useState} from 'react';
import {Link} from 'react-router';

import {dataTableViewActionT} from '@/lib/types/dataTableView.types';
import {statsRowT} from '@/lib/types/stats.types';

import {dateFormatter, usdFormatter} from '@/lib/helpers';
import {SupabaseInstance} from '@/lib/utils';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

import {StatsRowComponent} from '@/components';
import {Modal, ModalRefT} from '@/components/Modal';
import Page from '@/components/Page';
import {DataTableView} from '@/components/dataTableView';

/**
 * Admin Transactions page
 * This page displays all transactions in the system and allows admins to view, approve or deny them
 * @return {JSX.Element} - Admin Transactions page
 */
function AdminTransactionsPage() {
  // Hooks
  const {isAdmin} = useAuth();
  const {toast} = useNotifications();

  // State
  const [selectedTransaction, setSelectedTransaction] = useState<
    Database['public']['Views']['admin_transactions_with_speculators']['Row'] | null
  >(null);
  const [reasoning, setReasoning] = useState('');

  // Refs
  const modalRef = useRef<ModalRefT>(null);

  // stats for stats component
  const statsRow: statsRowT = [
    {
      title: 'Total Transactions',
      value: 150,
      subtitle: 'This Month',
      icon: 'ri-exchange-line',
      color: 'primary',
      change: 1.03,
      cardClass: 'Users-main-card primary',
    },
    {
      title: 'Pending Transactions',
      value: 45,
      subtitle: 'This Month',
      icon: 'ri-time-line',
      color: 'warning',
      change: 0.36,
      cardClass: 'Users-main-card warning',
    },
    {
      title: 'Completed Transactions',
      value: 105,
      subtitle: 'This Month',
      icon: 'ri-check-line',
      color: 'success',
      change: 2.28,
      cardClass: 'Users-main-card success',
    },
  ];

  const handleView = (row: Database['public']['Views']['admin_transactions_with_speculators']['Row']) => {
    setSelectedTransaction(row);
    modalRef.current?.show();
  };

  const handleApprove = async (approval: boolean) => {
    if (!selectedTransaction) return;
    try {
      const {data: authUser} = await SupabaseInstance.supabase.auth.getUser();
      if (!authUser || !authUser.user) throw new Error('User not found');

      const {error} = await SupabaseInstance.supabase.functions.invoke('transaction-approval', {
        body: {
          transaction_id: selectedTransaction.transaction_id,
          user_uid: authUser.user.id,
          approved: approval,
          reasoning: reasoning,
        },
      });

      if (error && error instanceof FunctionsHttpError) {
        const {
          error: {message},
        } = await error.context.json();

        toast.error(message);
      } else {
        toast.success('Transaction approval processed successfully');
      }
    } catch (error) {
      console.error('Error approving transaction', error);
    }
    // Handle approve logic
    modalRef.current?.hide();
  };

  const actions: dataTableViewActionT<'admin_transactions_with_speculators'>[] = [
    {
      icon: 'ri-eye-line',
      color: 'info',
      handler: row => handleView(row),
      permission: () => isAdmin,
    },
  ];

  return (
    <Page currentPage="Transactions">
      <StatsRowComponent stats={statsRow} />

      <DataTableView
        view="admin_transactions_with_speculators"
        title="Transactions"
        columnsDisplay={[
          {
            field: 'transaction_type',
            header: 'Type',
          },
          {
            field: 'transaction_status',
            header: 'Status',
          },
          {
            field: 'speculator_from_email',
            header: 'From',
          },
          {
            field: 'speculator_to_email',
            header: 'To',
          },
        ]}
        actionsDisplay={actions}
      />

      <Modal ref={modalRef} onClose={() => modalRef.current?.hide()} title="Transaction Details">
        {selectedTransaction && (
          <div className="box custom-box">
            <div className="box-body">
              <div className="mb-4 items-center sm:flex">
                <div className="flex items-center">
                  <div className="me-2">
                    <span className="avatar avatar-md avatar-rounded bg-light p-2">
                      <i className="bi bi-currency-dollar text-lg"></i>
                    </span>
                  </div>
                  <div className="mb-0 font-semibold">USD</div>
                </div>
              </div>
              <div className="items-end sm:flex">
                <div>
                  <p className="mb-1">USD</p>
                  <p className="mb-0 text-[1.25rem] font-semibold leading-none text-primary">
                    {usdFormatter.format(selectedTransaction.transaction_amount || 0)}
                  </p>
                </div>
                <div className="mt-2 sm:ms-auto sm:mt-0 sm:text-end">
                  <span
                    className={`badge bg-${
                      selectedTransaction.transaction_status === 'pending'
                        ? 'warning'
                        : selectedTransaction.transaction_status === 'failed'
                          ? 'danger'
                          : 'success'
                    }/10 text-${
                      selectedTransaction.transaction_status === 'pending'
                        ? 'warning'
                        : selectedTransaction.transaction_status === 'failed'
                          ? 'danger'
                          : 'success'
                    } ms-4`}
                  >
                    {selectedTransaction.transaction_status}
                  </span>
                  <p className="mb-0 text-[#8c9097] dark:text-white/50">
                    <span
                      className={`badge bg-${selectedTransaction.transaction_type === 'withdraw' ? 'warning' : 'success'}/10 text-${selectedTransaction.transaction_type === 'withdraw' ? 'warning' : 'success'} ms-4`}
                    >
                      {selectedTransaction.transaction_type}
                    </span>{' '}
                  </p>
                </div>
              </div>
            </div>
            <div className="box-footer !p-0">
              <div className="list-group border-0">
                <Link
                  to="#"
                  className="flex flex-col items-start border-x-0 !border-b border-t-0 !px-[1.25rem] py-3 dark:border-defaultborder/10"
                >
                  <div className="w-full items-center justify-between sm:flex">
                    <p className="tx-14 font-weight-semibold mb-0 text-dark">
                      Date of Transaction{' '}
                      <span className="badge ms-4 bg-primary/10 text-primary">
                        {(() => {
                          const created = new Date(selectedTransaction.date_created || '');
                          const now = new Date();
                          const diffMs = now.getTime() - created.getTime();
                          const diffMins = Math.floor(diffMs / (1000 * 60));

                          if (diffMins < 60) {
                            return `${diffMins} minutes ago`;
                          }

                          const diffHours = Math.floor(diffMins / 60);
                          if (diffHours < 24) {
                            return `${diffHours} hours ago`;
                          }

                          const diffDays = Math.floor(diffHours / 24);
                          return `${diffDays} days ago`;
                        })()}
                      </span>
                    </p>
                    <p className="font-weight-normal tx-13 mb-0 text-success">
                      <span className="numberfont">
                        {dateFormatter(selectedTransaction.date_created || '', 'long')}
                      </span>
                    </p>
                  </div>
                </Link>
                {selectedTransaction.transaction_type === 'withdraw' && (
                  <Link to="#" className="flex flex-col items-start border-x-0 border-t-0 !px-[1.25rem] py-3">
                    <div className="flex w-full items-center justify-between">
                      <p className="tx-14 font-weight-semibold mb-0 text-dark">From Account</p>
                      <p className="tx-15 mb-0 text-dark">
                        <span className="numberfont">{selectedTransaction.speculator_from_email}</span>
                      </p>
                    </div>
                  </Link>
                )}
                {selectedTransaction.transaction_type === 'deposit' && (
                  <Link to="#" className="flex flex-col items-start border-x-0 border-t-0 !px-[1.25rem] py-3">
                    <div className="flex w-full items-center justify-between">
                      <p className="tx-14 font-weight-semibold mb-0 text-dark">To Account</p>
                      <p className="tx-15 mb-0 text-dark">
                        <span className="numberfont">{selectedTransaction.speculator_to_email}</span>
                      </p>
                    </div>
                  </Link>
                )}
                {selectedTransaction.transaction_type === 'transfer' && (
                  <>
                    <Link to="#" className="flex flex-col items-start border-x-0 border-t-0 !px-[1.25rem] py-3">
                      <div className="flex w-full items-center justify-between">
                        <p className="tx-14 font-weight-semibold mb-0 text-dark">From Account</p>
                        <p className="tx-15 mb-0 text-dark">
                          <span className="numberfont">{selectedTransaction.speculator_from_email}</span>
                        </p>
                      </div>
                    </Link>
                    <Link to="#" className="flex flex-col items-start border-x-0 border-t-0 !px-[1.25rem] py-3">
                      <div className="flex w-full items-center justify-between">
                        <p className="tx-14 font-weight-semibold mb-0 text-dark">To Account</p>
                        <p className="tx-15 mb-0 text-dark">
                          <span className="numberfont">{selectedTransaction.speculator_to_email}</span>
                        </p>
                      </div>
                    </Link>
                  </>
                )}
              </div>
            </div>
            {selectedTransaction.transaction_type === 'withdraw' &&
              selectedTransaction.transaction_status === 'pending' && (
                <>
                  <input
                    className="form-control"
                    type="text"
                    value={reasoning}
                    placeholder="Reasoning for approval or denial"
                    onChange={e => setReasoning(e.target.value)}
                  />
                  <div className="ml-auto mt-4 flex gap-4">
                    <button
                      onClick={() => handleApprove(false)}
                      className="ti-btn ti-btn-danger !m-0 !gap-0 !bg-danger !px-2 !py-1 !text-[0.75rem] !font-medium !text-white"
                    >
                      Deny
                      <i className="ri-close-line"></i>
                    </button>
                    <button
                      onClick={() => handleApprove(true)}
                      className="ti-btn ti-btn-success !m-0 !gap-0 !bg-success !px-2 !py-1 !text-[0.75rem] !font-medium !text-white"
                    >
                      Approve
                      <i className="ri-check-line"></i>
                    </button>
                  </div>
                </>
              )}
          </div>
        )}
      </Modal>
    </Page>
  );
}

export default AdminTransactionsPage;
